import { AGREEMENT_NEW_BUILD_REPORT_TITLE_EN, AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH, AGREEMENT_NEW_BUILD_TITLE_EN } from './agreementText';
import { newId } from './ids';
import { V2_SCHEMA_VERSION } from './inspection';
import type { InspectionService } from './inspectionService';

// Final report records (Owner task book 2026-09-04 §18). The Report Language is chosen only at the
// end: English → ONE English client PDF; Chinese → TWO PDFs (the English client report + a Chinese
// copy the inspector keeps). A single bilingual PDF is never produced. Every generated PDF is kept
// as a record (bytes in the media store under `report:<id>`), and generating the final report locks
// the inspection (`V2Inspection.finalizedAt`).
export type ReportOutputLanguage = 'en-US' | 'zh-CN';

export const REPORT_OUTPUT_LANGUAGES: readonly ReportOutputLanguage[] = Object.freeze(['en-US', 'zh-CN']);

export function isReportOutputLanguage(value: unknown): value is ReportOutputLanguage {
  return value === 'en-US' || value === 'zh-CN';
}

// 'agreement' = the signed Inspection Agreement, always its OWN PDF (Round 2 §AK): never merged
// into the report and never rewritten at report time.
// 'clientChineseSummary' = the one-page-or-so Chinese summary FOR THE CLIENT (Owner decision list
// 2026-10-19 item 23): the Summary of Deficiencies in Chinese, saying the English report governs. Its
// own PDF, produced only when the inspector switches it on for this inspection.
export type ReportVariant = 'clientEnglish' | 'selfChinese' | 'clientChineseSummary' | 'agreement';

export const REPORT_VARIANT_ORDER: readonly ReportVariant[] = Object.freeze(['clientEnglish', 'selfChinese', 'clientChineseSummary', 'agreement']);

export function isReportVariant(value: unknown): value is ReportVariant {
  return value === 'clientEnglish' || value === 'selfChinese' || value === 'clientChineseSummary' || value === 'agreement';
}

// 'addendum' = a dated, signed 「补充说明 / Addendum」 issued AFTER delivery (Owner decision list 2026-10-19
// item 27): the original report stays exactly as delivered; the addendum states which item it corrects, why,
// and the correction. A file kind of its own, never one of a generation run's outputs.
export type ReportFileVariant = ReportVariant | 'addendum';

export function isReportFileVariant(value: unknown): value is ReportFileVariant {
  return value === 'addendum' || isReportVariant(value);
}

// Which PDFs one generation run produces for the chosen Report Language.
export function variantsForLanguage(language: ReportOutputLanguage): ReportVariant[] {
  return language === 'zh-CN' ? ['clientEnglish', 'selfChinese'] : ['clientEnglish'];
}

// The language a variant is rendered in (the client copy and the agreement are always English).
export function variantLocale(variant: ReportVariant): ReportOutputLanguage {
  return variant === 'selfChinese' || variant === 'clientChineseSummary' ? 'zh-CN' : 'en-US';
}

export interface V2ReportRecord {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  // The Report Language the inspector chose for this generation run.
  language: ReportOutputLanguage;
  variant: ReportFileVariant;
  fileName: string;
  mimeType: 'application/pdf';
  byteSize: number;
  pageCount: number;
  generatedAt: number;
}

function safeFileStem(text: string): string {
  const cleaned = text.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.length > 0 ? cleaned : 'Inspection';
}

// The file label per service and variant. A New Build observation must never produce a file called
// "Inspection Report" or 「验屋报告」 (OD-9 / approved draft §4.1) — the file name is part of what the
// client receives, so the naming follows the service, not the template.
const FILE_LABELS: Record<InspectionService, Record<ReportVariant, string>> = Object.freeze({
  homeInspection: Object.freeze({ clientEnglish: 'Inspection Report', selfChinese: '验屋报告（自留）', clientChineseSummary: '验屋报告中文摘要', agreement: 'Inspection Agreement' }),
  newBuild: Object.freeze({ clientEnglish: AGREEMENT_NEW_BUILD_REPORT_TITLE_EN, selfChinese: `${AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH}（自留）`, clientChineseSummary: `${AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH}中文摘要`, agreement: AGREEMENT_NEW_BUILD_TITLE_EN })
});

// `<Address> - Inspection Report - <YYYY-MM-DD>.pdf` (client copy) ·
// `<Address> - 验屋报告（自留）- <YYYY-MM-DD>.pdf` (inspector's Chinese copy) ·
// `<Address> - 验屋报告中文摘要 - <YYYY-MM-DD>.pdf` (the client's Chinese summary, when switched on) ·
// `<Address> - Inspection Agreement - <YYYY-MM-DD>.pdf` (the signed agreement) ·
// the New Build variant of each (OD-9) ·
// a Demo inspection's files carry " (DEMO)" so they can never pass as client deliverables.
// The product name (Owner D-1, 2026-09-11) is deliberately NOT in the file name: the deliverable
// must not read as AI-authored. Open for the Owner to rule on (ledger 2026-09-11 Q-1).
export function reportFileName(address: string, generatedAt: number, variant: ReportVariant, demo: boolean = false, service: InspectionService = 'homeInspection'): string {
  const d = new Date(generatedAt);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${safeFileStem(address)} - ${FILE_LABELS[service][variant]} - ${date}${demo ? ' (DEMO)' : ''}.pdf`;
}

// `<Address> - Inspection Report Addendum 1 - <YYYY-MM-DD>.pdf` (the New Build title for a New Build
// observation; " (DEMO)" on a Demo inspection) — numbered per inspection, in the order issued.
export function addendumFileName(address: string, issuedAt: number, number: number, demo: boolean = false, service: InspectionService = 'homeInspection'): string {
  const d = new Date(issuedAt);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${safeFileStem(address)} - ${FILE_LABELS[service].clientEnglish} Addendum ${number} - ${date}${demo ? ' (DEMO)' : ''}.pdf`;
}

export interface NewReportRecordInput {
  inspectionId: string;
  language: ReportOutputLanguage;
  variant: ReportFileVariant;
  fileName: string;
  byteSize: number;
  pageCount: number;
}

export function createReportRecord(input: NewReportRecordInput, now: number): V2ReportRecord {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('rpt'),
    inspectionId: input.inspectionId,
    language: input.language,
    variant: input.variant,
    fileName: input.fileName,
    mimeType: 'application/pdf',
    byteSize: input.byteSize,
    pageCount: input.pageCount,
    generatedAt: now
  };
}

export const REPORT_MEDIA_PREFIX = 'report:';

export function reportMediaKey(reportId: string): string {
  return `${REPORT_MEDIA_PREFIX}${reportId}`;
}

// The signed agreement's PDF, made as soon as both parties signed (src/state/agreementPdfStore.ts).
export function agreementPdfMediaKey(inspectionId: string): string {
  return `agreement-pdf:${inspectionId}`;
}
